import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Spin
} from "antd";
import {
  createSprTov,
  fetchSprTov,
  fetchSprTovKomplekts,
  saveSprTov
} from "./spr-tov-api";

const LABEL_WIDTH = 132;
const NUM_WIDTH = 80;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const numStyle = { width: NUM_WIDTH };

const VID_PLAN_OPTIONS = [
  { value: 0, label: "Не планировать" },
  { value: 1, label: "Каждый день" },
  { value: 2, label: "Через день" },
  { value: 7, label: "Раз в неделю" },
  { value: 100, label: "По времени" }
];

const USE_REST_OPTIONS = [
  { value: 0, label: "Услуга" },
  { value: 1, label: "Товар" },
  { value: 2, label: "Комплект" },
  { value: 4, label: "Исследования" }
];

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toFlag = (value) => Number(value) === 1;

const normalizeCard = (row) => ({
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  idUpper: toNumber(pick(row, "idUpper", "IdUpper")) ?? 0,
  isGroup: toFlag(pick(row, "isGroup", "IsGroup")),
  name: pick(row, "name", "Name") ?? "",
  funit: pick(row, "funit", "Funit") ?? "",
  art: pick(row, "art", "Art") ?? "",
  shk: pick(row, "shk", "Shk") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  koeff: toNumber(pick(row, "koeff", "Koeff")),
  zenaExp: toNumber(pick(row, "zenaExp", "ZenaExp")),
  zenaPut: toNumber(pick(row, "zenaPut", "ZenaPut")),
  timeMax: toNumber(pick(row, "timeMax", "TimeMax")),
  restTime: toNumber(pick(row, "restTime", "RestTime")),
  vidPlan: toNumber(pick(row, "vidPlan", "VidPlan")) ?? 1,
  prcHdc: toNumber(pick(row, "prcHdc", "PrcHdc")),
  prcZpl: toNumber(pick(row, "prcZpl", "PrcZpl")),
  prcAssign: toNumber(pick(row, "prcAssign", "PrcAssign")),
  prcDone: toNumber(pick(row, "prcDone", "PrcDone")),
  isHide: toFlag(pick(row, "isHide", "IsHide")),
  sexControl: toFlag(pick(row, "sexControl", "SexControl")),
  uet: toNumber(pick(row, "uet", "Uet")),
  depno: toNumber(pick(row, "depno", "Depno")) ?? 1,
  useRest: toNumber(pick(row, "useRest", "UseRest")) ?? 0,
  idKompl: toNumber(pick(row, "idKompl", "IdKompl")),
  sync: toFlag(pick(row, "sync", "Sync")),
  isPlat: toFlag(pick(row, "isPlat", "IsPlat")),
  vidKkm: toNumber(pick(row, "vidKkm", "VidKkm")) ?? 0,
  mark: toFlag(pick(row, "mark", "Mark")),
  labEnabled: toFlag(pick(row, "labEnabled", "LabEnabled")),
  labId: toNumber(pick(row, "labId", "LabId")),
  labCode: pick(row, "labCode", "LabCode") ?? "",
  childCount: toNumber(pick(row, "childCount", "ChildCount")) ?? 0
});

const emptyCard = (idUpper, isGroup) => ({
  idTov: null,
  idUpper: idUpper == null || idUpper < 0 ? 0 : idUpper,
  isGroup: Boolean(isGroup),
  name: "",
  funit: "",
  art: "",
  shk: "",
  komment: "",
  koeff: 0,
  zenaExp: 0,
  zenaPut: 0,
  timeMax: 0,
  restTime: 0,
  vidPlan: 1,
  prcHdc: 0,
  prcZpl: 0,
  prcAssign: 0,
  prcDone: 0,
  isHide: false,
  sexControl: false,
  uet: 0,
  depno: 1,
  useRest: 0,
  idKompl: null,
  sync: false,
  isPlat: false,
  vidKkm: 0,
  mark: false,
  labEnabled: false,
  labId: null,
  labCode: "",
  childCount: 0
});

const cardToFormValues = (card) => ({
  ...card,
  idUpper: card.idUpper == null || card.idUpper < 0 ? 0 : card.idUpper,
  idKompl: card.idKompl && card.idKompl > 0 ? card.idKompl : undefined
});

const flag = (value) => (value ? 1 : 0);

/**
 * Вкладка «Основная» (поля как Delphi TFrmETovGroup / TFrmESprTovSan).
 */
const SprTovMain = ({
  idTov,
  isNew = false,
  isGroupNew = false,
  idUpper,
  open,
  parentOptions = [],
  onSaved,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isGroup, setIsGroup] = useState(Boolean(isGroupNew));
  const [komplekts, setKomplekts] = useState([]);
  const useRest = Form.useWatch("useRest", form);
  const vidPlan = Form.useWatch("vidPlan", form);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchSprTovKomplekts()
      .then((data) => {
        if (!cancelled) setKomplekts(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setKomplekts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    if (isNew) {
      const card = emptyCard(idUpper, isGroupNew);
      setIsGroup(card.isGroup);
      setError("");
      setLoading(false);
      form.setFieldsValue(cardToFormValues(card));
      return () => {
        cancelled = true;
      };
    }

    if (idTov == null) return undefined;

    setLoading(true);
    setError("");
    fetchSprTov(idTov)
      .then((row) => {
        if (cancelled) return;
        const card = normalizeCard(row);
        setIsGroup(card.isGroup);
        form.setFieldsValue(cardToFormValues(card));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить услугу");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idTov, idUpper, isGroupNew, form]);

  const parentSelectOptions = useMemo(
    () => [
      { value: 0, label: "— корень —" },
      ...(parentOptions || [])
        .filter((o) => o.value !== idTov)
        .map((o) => ({ value: o.value, label: o.label }))
    ],
    [parentOptions, idTov]
  );

  const komplektOptions = useMemo(
    () =>
      (komplekts || [])
        .map((k) => ({
          value: toNumber(pick(k, "idKompl", "IdKompl")),
          label: pick(k, "name", "Name") ?? ""
        }))
        .filter((o) => o.value != null && o.value > 0),
    [komplekts]
  );

  const onFinish = async (values) => {
    setSaving(true);
    setError("");
    const body = isGroup
      ? {
          ...(isNew ? {} : { idTov }),
          isGroup: 1,
          idUpper: values.idUpper == null || values.idUpper < 0 ? 0 : values.idUpper,
          name: (values.name || "").trim(),
          labCode: values.labCode || null,
          labId: values.labId ?? null
        }
      : {
          ...(isNew ? {} : { idTov }),
          isGroup: 0,
          idUpper: values.idUpper == null || values.idUpper < 0 ? 0 : values.idUpper,
          name: (values.name || "").trim(),
          funit: values.funit || null,
          art: values.art || null,
          shk: values.shk || null,
          komment: values.komment || null,
          koeff: values.koeff ?? 0,
          zenaExp: values.zenaExp ?? 0,
          zenaPut: values.zenaPut ?? 0,
          timeMax: values.timeMax ?? 0,
          restTime: values.restTime ?? 0,
          vidPlan: values.vidPlan ?? 1,
          prcHdc: values.prcHdc ?? 0,
          prcZpl: values.prcZpl ?? 0,
          prcAssign: values.prcAssign ?? 0,
          prcDone: values.prcDone ?? 0,
          isHide: flag(values.isHide),
          sexControl: flag(values.sexControl),
          uet: values.uet ?? 0,
          depno: values.depno ?? 1,
          useRest: values.useRest ?? 0,
          idKompl: values.useRest === 2 ? values.idKompl || 0 : values.idKompl || 0,
          sync: flag(values.sync),
          isPlat: flag(values.isPlat),
          vidKkm: values.vidKkm ?? 0,
          mark: flag(values.mark),
          labEnabled: flag(values.labEnabled),
          labId: values.labId ?? null,
          labCode: values.labCode || null
        };

    try {
      if (isNew) {
        const created = await createSprTov(body);
        onSaved?.(created);
      } else {
        const saved = await saveSprTov(body);
        onSaved?.(saved);
      }
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="spr-tov-card__tab">
        <div className="spr-tov-card__center">
          <Spin tip="Загрузка..." />
        </div>
      </div>
    );
  }

  return (
    <div className="spr-tov-card__tab">
      <div className="spr-tov-card__body">
        {error ? (
          <Alert className="spr-tov-card__error" type="error" showIcon message={error} />
        ) : null}
        <Form
          form={form}
          className="spr-tov-card__form"
          size="small"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          disabled={saving}
        >
          {!isNew ? (
            <Form.Item label="Код" name="idTov">
              <InputNumber style={numStyle} disabled controls={false} />
            </Form.Item>
          ) : null}
          <Form.Item
            label="Наименование"
            name="name"
            rules={[{ required: true, message: "Укажите наименование" }]}
          >
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item label="Группа" name="idUpper">
            <Select options={parentSelectOptions} showSearch optionFilterProp="label" />
          </Form.Item>

          {isGroup ? (
            <>
              <div className="spr-tov-card__section">Лаборатория (опц.)</div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Код" name="labCode">
                  <Input maxLength={80} />
                </Form.Item>
                <Form.Item label="Lab Id" name="labId">
                  <InputNumber style={numStyle} min={0} precision={0} />
                </Form.Item>
              </div>
            </>
          ) : (
            <>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Артикул" name="art">
                  <Input maxLength={80} />
                </Form.Item>
                <Form.Item label="Вид" name="useRest">
                  <Select options={USE_REST_OPTIONS} />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Ед. изм." name="funit">
                  <Input maxLength={80} />
                </Form.Item>
                <Form.Item label="Отдел" name="depno">
                  <InputNumber style={numStyle} min={1} precision={0} />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="План" name="vidPlan">
                  <Select options={VID_PLAN_OPTIONS} />
                </Form.Item>
                <Form.Item label="Длит., мин" name="timeMax">
                  <InputNumber
                    style={numStyle}
                    min={0}
                    precision={0}
                    disabled={vidPlan !== 100}
                  />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item
                  className="spr-tov-card__check"
                  label=" "
                  name="sexControl"
                  valuePropName="checked"
                >
                  <Checkbox>Контроль пола</Checkbox>
                </Form.Item>
                <Form.Item label="Отдых, мин" name="restTime">
                  <InputNumber style={numStyle} min={0} precision={0} />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="% НДС" name="prcHdc">
                  <InputNumber style={numStyle} min={0} />
                </Form.Item>
                <Form.Item label="УЕТ" name="uet">
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Цена" name="zenaExp">
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
                <Form.Item
                  label="Комплект"
                  name="idKompl"
                  rules={
                    useRest === 2
                      ? [{ required: true, message: "Выберите комплект" }]
                      : undefined
                  }
                >
                  <Select
                    allowClear
                    disabled={useRest !== 2}
                    options={komplektOptions}
                    showSearch
                    optionFilterProp="label"
                    placeholder={useRest === 2 ? "Выберите комплект" : "—"}
                  />
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item
                  className="spr-tov-card__check"
                  label=" "
                  name="isHide"
                  valuePropName="checked"
                >
                  <Checkbox>Удалена</Checkbox>
                </Form.Item>
                <Form.Item label="Штрихкод" name="shk">
                  <Input maxLength={80} />
                </Form.Item>
              </div>
              <Form.Item
                className="spr-tov-card__check"
                label=" "
                name="isPlat"
                valuePropName="checked"
              >
                <Checkbox>Платно по умолчанию</Checkbox>
              </Form.Item>
              <div className="spr-tov-card__salary">
                <Form.Item label="За продажу" name="prcZpl">
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
                <Form.Item
                  label="За назначение"
                  name="prcAssign"
                  labelCol={{ flex: "0 0 96px" }}
                >
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
                <Form.Item
                  label="За выполнение"
                  name="prcDone"
                  labelCol={{ flex: "0 0 96px" }}
                >
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
                <span className="spr-tov-card__salary-caption">% зарплаты</span>
              </div>
              <Form.Item label="Комментарий" name="komment">
                <Input.TextArea rows={2} />
              </Form.Item>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Цена прих." name="zenaPut">
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
                <Form.Item label="Коэфф." name="koeff">
                  <InputNumber style={numStyle} min={0} step={0.01} />
                </Form.Item>
              </div>
              <div className="spr-tov-card__checks">
                <Form.Item name="sync" valuePropName="checked" noStyle>
                  <Checkbox>Синхронизация</Checkbox>
                </Form.Item>
                <Form.Item name="mark" valuePropName="checked" noStyle>
                  <Checkbox>Маркировка</Checkbox>
                </Form.Item>
                <Form.Item name="labEnabled" valuePropName="checked" noStyle>
                  <Checkbox>Лаборатория</Checkbox>
                </Form.Item>
              </div>
              <div className="spr-tov-card__row spr-tov-card__row--2">
                <Form.Item label="Код лаб." name="labCode">
                  <Input maxLength={80} />
                </Form.Item>
                <Form.Item label="Lab Id" name="labId">
                  <InputNumber style={numStyle} min={0} precision={0} />
                </Form.Item>
              </div>
              <Form.Item label="ККМ" name="vidKkm">
                <InputNumber style={numStyle} min={0} precision={0} />
              </Form.Item>
            </>
          )}
        </Form>
      </div>
      <div className="spr-tov-card__footer">
        <Button type="primary" loading={saving} onClick={() => form.submit()}>
          ОК
        </Button>
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprTovMain;
